import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { statusLabel, statusStyle } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Box, Stethoscope, Link2, Plus, Trash2, CalendarClock, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const DAYS = [["0", "Lundi"], ["1", "Mardi"], ["2", "Mercredi"], ["3", "Jeudi"], ["4", "Vendredi"], ["5", "Samedi"], ["6", "Dimanche"]];
const DURATION_OPTIONS = [15, 20, 30, 45, 60];
const PREVENTION_MODES = [{ v: "facultatif", l: "Facultatif" }, { v: "obligatoire", l: "Obligatoire" }, { v: "masque", l: "Masqué" }];
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const dtf = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—";
const hm = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—";

function CabinEditor({ cabin, onSaved }) {
  const [hours, setHours] = useState(cabin.openingHours || {});
  const [closures, setClosures] = useState(cabin.exceptionalClosures || []);
  const [newClosure, setNewClosure] = useState("");
  const [form, setForm] = useState({
    name: cabin.name, active: cabin.active,
    defaultDurationMinutes: cabin.defaultDurationMinutes || 30,
    marginBeforeMinutes: cabin.marginBeforeMinutes ?? 10,
    marginAfterMinutes: cabin.marginAfterMinutes ?? 10,
  });

  const setInterval = (day, idx, pos, val) => {
    setHours((h) => {
      const list = (h[day] || []).map((iv) => [...iv]);
      list[idx][pos] = val;
      return { ...h, [day]: list };
    });
  };
  const addInterval = (day) => setHours((h) => ({ ...h, [day]: [...(h[day] || []), ["09:00", "12:00"]] }));
  const removeInterval = (day, idx) => setHours((h) => ({ ...h, [day]: (h[day] || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    try {
      await api.patch(`/admin/cabins/${cabin.id}`, { ...form, defaultDurationMinutes: Number(form.defaultDurationMinutes),
        marginBeforeMinutes: Number(form.marginBeforeMinutes), marginAfterMinutes: Number(form.marginAfterMinutes),
        openingHours: hours, exceptionalClosures: closures });
      toast.success("Cabine enregistrée"); onSaved?.();
    } catch (e) { err(e); }
  };

  return (
    <Card className="rounded-2xl p-5 space-y-5" data-testid={`cabin-editor-${cabin.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`cabin-name-${cabin.id}`} className="h-11 rounded-xl font-heading font-semibold max-w-xs" />
        <div className="flex items-center gap-2"><Label className="text-xs">Active</Label>
          <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid={`cabin-active-${cabin.id}`} /></div>
        <Badge className={form.active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-0" : "bg-slate-100 text-slate-500 border-0"}>{form.active ? "Active" : "Inactive"}</Badge>
        {cabin.isTest && <Badge className="bg-amber-100 text-amber-800 border-0 dark:bg-amber-950 dark:text-amber-300" data-testid={`cabin-test-badge-${cabin.id}`}>Mode test</Badge>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Durée par défaut</Label>
          <Select value={String(form.defaultDurationMinutes)} onValueChange={(v) => setForm({ ...form, defaultDurationMinutes: Number(v) })}>
            <SelectTrigger data-testid={`cabin-duration-${cabin.id}`} className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{DURATION_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Marge avant (min)</Label>
          <Input type="number" value={form.marginBeforeMinutes} onChange={(e) => setForm({ ...form, marginBeforeMinutes: e.target.value })} data-testid={`cabin-before-${cabin.id}`} className="h-11 rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Marge nettoyage après (min)</Label>
          <Input type="number" value={form.marginAfterMinutes} onChange={(e) => setForm({ ...form, marginAfterMinutes: e.target.value })} data-testid={`cabin-after-${cabin.id}`} className="h-11 rounded-xl" /></div>
      </div>

      <div>
        <Label className="text-xs mb-2 block">Horaires d'ouverture (plusieurs plages = pauses)</Label>
        <div className="space-y-2">
          {DAYS.map(([d, label]) => (
            <div key={d} className="flex items-start gap-3 rounded-xl border p-2.5">
              <span className="w-20 text-sm font-medium pt-2">{label}</span>
              <div className="flex-1 space-y-1.5">
                {(hours[d] || []).length === 0 && <span className="text-xs text-muted-foreground pt-2 inline-block">Fermé</span>}
                {(hours[d] || []).map((iv, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="time" value={iv[0]} onChange={(e) => setInterval(d, i, 0, e.target.value)} data-testid={`hours-${cabin.id}-${d}-${i}-start`} className="h-9 w-28 rounded-lg" />
                    <span className="text-muted-foreground">→</span>
                    <Input type="time" value={iv[1]} onChange={(e) => setInterval(d, i, 1, e.target.value)} data-testid={`hours-${cabin.id}-${d}-${i}-end`} className="h-9 w-28 rounded-lg" />
                    <Button size="icon" variant="ghost" onClick={() => removeInterval(d, i)} className="h-8 w-8 rounded-lg text-muted-foreground"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => addInterval(d)} data-testid={`add-interval-${cabin.id}-${d}`} className="rounded-lg h-9 gap-1"><Plus className="h-3.5 w-3.5" /> Plage</Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs mb-2 block">Fermetures exceptionnelles</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {closures.map((c) => (
            <Badge key={c} variant="outline" className="gap-1.5 py-1.5">{c}
              <button onClick={() => setClosures(closures.filter((x) => x !== c))} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
            </Badge>
          ))}
          {closures.length === 0 && <span className="text-xs text-muted-foreground">Aucune</span>}
        </div>
        <div className="flex gap-2">
          <Input type="date" value={newClosure} onChange={(e) => setNewClosure(e.target.value)} data-testid={`closure-input-${cabin.id}`} className="h-10 rounded-xl max-w-[180px]" />
          <Button variant="outline" onClick={() => { if (newClosure && !closures.includes(newClosure)) { setClosures([...closures, newClosure].sort()); setNewClosure(""); } }} data-testid={`add-closure-${cabin.id}`} className="rounded-xl h-10 gap-1"><Plus className="h-4 w-4" /> Ajouter</Button>
        </div>
      </div>

      <Button onClick={save} data-testid={`cabin-save-${cabin.id}`} className="rounded-xl h-11 gap-2"><Save className="h-4 w-4" /> Enregistrer la cabine</Button>
    </Card>
  );
}

function CabinsTab() {
  const [cabins, setCabins] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/admin/cabins").then((r) => setCabins(r.data)).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/admin/cabins", { name: name.trim(), active: true }); setName(""); load(); toast.success("Cabine créée"); }
    catch (e) { err(e); }
  };
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl p-4">
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cabin-new-name" placeholder="Nom d'une nouvelle cabine" className="h-11 rounded-xl" />
          <Button onClick={create} data-testid="cabin-create" className="rounded-xl h-11 gap-1.5"><Plus className="h-4 w-4" /> Créer</Button>
        </div>
      </Card>
      <div className="space-y-4" data-testid="cabins-list">
        {cabins.map((c) => <CabinEditor key={c.id} cabin={c} onSaved={load} />)}
      </div>
    </div>
  );
}

const SLOT_STATUS_META = {
  AVAILABLE: { l: "Disponible", c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
  HELD: { l: "En attente", c: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
  RESERVED: { l: "Réservé", c: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" },
  BLOCKED: { l: "Bloqué", c: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300" },
  COMPLETED: { l: "Terminé", c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
  CANCELLED: { l: "Annulé", c: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300" },
};
const GEN_DURATIONS = [15, 30, 45, 60];
const todayStr = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });

function AgendaTab() {
  const confirm = useConfirm();
  const [cabins, setCabins] = useState([]);
  const [cabinId, setCabinId] = useState("");
  const [gen, setGen] = useState({ startDate: todayStr(), endDate: todayStr(), durationMinutes: 30 });
  const [manual, setManual] = useState({ date: todayStr(), time: "09:00", durationMinutes: 30 });
  const [viewDate, setViewDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/cabins").then((r) => { setCabins(r.data); if (r.data[0]) setCabinId(r.data[0].id); }).catch(err);
  }, []);

  const loadSlots = useCallback(() => {
    if (!cabinId) return;
    api.get(`/admin/slots?cabinId=${cabinId}&date=${viewDate}`).then((r) => setSlots(r.data.slots || [])).catch(err);
  }, [cabinId, viewDate]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const doGenerate = async () => {
    if (!cabinId) { toast.error("Choisissez une cabine."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/slots/generate", { cabinId, ...gen, durationMinutes: Number(gen.durationMinutes) });
      toast.success(`${data.created} créneau(x) généré(s)`); loadSlots();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const addManual = async () => {
    if (!cabinId) { toast.error("Choisissez une cabine."); return; }
    try { await api.post("/admin/slots", { cabinId, ...manual, durationMinutes: Number(manual.durationMinutes) }); toast.success("Créneau ajouté"); setViewDate(manual.date); loadSlots(); }
    catch (e) { err(e); }
  };
  const patchSlot = async (id, status) => { try { await api.patch(`/admin/slots/${id}`, { status }); loadSlots(); } catch (e) { err(e); } };
  const delSlot = async (id) => { try { await api.delete(`/admin/slots/${id}`); loadSlots(); } catch (e) { err(e); } };
  const clearDay = async () => {
    if (!(await confirm({ title: "Vider le jour", description: "Supprimer tous les créneaux non réservés de ce jour ?", confirmText: "Supprimer", destructive: true }))) return;
    try { const { data } = await api.post("/admin/slots/clear", { cabinId, date: viewDate }); toast.success(`${data.deleted} créneau(x) supprimé(s)`); loadSlots(); }
    catch (e) { err(e); }
  };

  return (
    <div className="space-y-4" data-testid="agenda-tab">
      <Card className="rounded-2xl p-4">
        <Label className="text-xs mb-1.5 block">Cabine</Label>
        <Select value={cabinId} onValueChange={setCabinId}>
          <SelectTrigger data-testid="agenda-cabin-select" className="h-11 rounded-xl max-w-sm"><SelectValue placeholder="Choisir une cabine" /></SelectTrigger>
          <SelectContent>{cabins.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      <Card className="rounded-2xl p-5 space-y-3">
        <div className="font-heading font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Générer des créneaux</div>
        <p className="text-xs text-muted-foreground">Les créneaux sont créés dans les horaires d'ouverture de la cabine. Les créneaux existants ne sont pas dupliqués.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Du</Label>
            <Input type="date" value={gen.startDate} onChange={(e) => setGen({ ...gen, startDate: e.target.value })} data-testid="gen-start" className="h-11 rounded-xl" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Au</Label>
            <Input type="date" value={gen.endDate} onChange={(e) => setGen({ ...gen, endDate: e.target.value })} data-testid="gen-end" className="h-11 rounded-xl" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Durée</Label>
            <Select value={String(gen.durationMinutes)} onValueChange={(v) => setGen({ ...gen, durationMinutes: Number(v) })}>
              <SelectTrigger data-testid="gen-duration" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{GEN_DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="flex items-end"><Button onClick={doGenerate} disabled={busy} data-testid="gen-submit" className="rounded-xl h-11 w-full gap-1.5"><CalendarClock className="h-4 w-4" /> Générer</Button></div>
        </div>
      </Card>

      <Card className="rounded-2xl p-5 space-y-3">
        <div className="font-heading font-semibold flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Ajouter un créneau manuel</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Date</Label>
            <Input type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} data-testid="manual-date" className="h-11 rounded-xl" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Heure</Label>
            <Input type="time" value={manual.time} onChange={(e) => setManual({ ...manual, time: e.target.value })} data-testid="manual-time" className="h-11 rounded-xl" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Durée</Label>
            <Select value={String(manual.durationMinutes)} onValueChange={(v) => setManual({ ...manual, durationMinutes: Number(v) })}>
              <SelectTrigger data-testid="manual-duration" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{GEN_DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="flex items-end"><Button onClick={addManual} data-testid="manual-submit" variant="outline" className="rounded-xl h-11 w-full gap-1.5"><Plus className="h-4 w-4" /> Ajouter</Button></div>
        </div>
      </Card>

      <Card className="rounded-2xl p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Jour</Label>
            <Input type="date" value={viewDate} onChange={(e) => setViewDate(e.target.value)} data-testid="agenda-view-date" className="h-10 rounded-xl max-w-[180px]" />
          </div>
          <Button variant="outline" onClick={clearDay} data-testid="agenda-clear" className="rounded-xl h-10 gap-1.5 text-destructive"><Trash2 className="h-4 w-4" /> Vider le jour (non réservés)</Button>
        </div>
        <div className="space-y-2" data-testid="agenda-slots-list">
          {slots.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Aucun créneau ce jour. Générez ou ajoutez-en.</p>}
          {slots.map((s) => {
            const meta = SLOT_STATUS_META[s.status] || SLOT_STATUS_META.AVAILABLE;
            return (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3" data-testid={`agenda-slot-${s.id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-sm">{hm(s.startAt)} – {hm(s.endAt)}</span>
                  <span className="text-xs text-muted-foreground">{s.durationMinutes} min</span>
                  {s.patient && <span className="text-xs text-muted-foreground">· {s.patient}</span>}
                  <Badge className={`${meta.c} border-0`}>{meta.l}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  {s.status === "AVAILABLE" && <Button size="sm" variant="outline" onClick={() => patchSlot(s.id, "BLOCKED")} data-testid={`block-${s.id}`} className="rounded-lg h-8 text-xs">Bloquer</Button>}
                  {s.status === "BLOCKED" && <Button size="sm" variant="outline" onClick={() => patchSlot(s.id, "AVAILABLE")} data-testid={`unblock-${s.id}`} className="rounded-lg h-8 text-xs">Débloquer</Button>}
                  {(s.status === "RESERVED" || s.status === "HELD") && <Button size="sm" variant="outline" onClick={() => patchSlot(s.id, "AVAILABLE")} data-testid={`cancel-${s.id}`} className="rounded-lg h-8 text-xs text-destructive">Annuler</Button>}
                  {!["RESERVED", "HELD"].includes(s.status) && <Button size="icon" variant="ghost" onClick={() => delSlot(s.id)} data-testid={`del-${s.id}`} className="h-8 w-8 rounded-lg text-muted-foreground"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function BookingsTab() {
  const confirm = useConfirm();
  const [bookings, setBookings] = useState([]);
  const load = useCallback(() => api.get("/admin/bookings").then((r) => setBookings(r.data.bookings || [])).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const delBooking = async (id) => {
    if (!(await confirm({ title: "Supprimer la réservation", description: "Supprimer définitivement cette réservation ? Le créneau de cabine sera libéré. Action irréversible.", confirmText: "Supprimer", destructive: true }))) return;
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Réservation supprimée."); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-2" data-testid="bookings-list">
      {bookings.length === 0 && <Card className="rounded-2xl p-8 text-center text-muted-foreground">Aucune réservation active.</Card>}
      {bookings.map((b) => (
        <Card key={b.id} className="rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`booking-${b.id}`}>
          <div>
            <div className="font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> {b.patient || "—"} · {b.cabinName}</div>
            <div className="text-sm text-muted-foreground">{dtf(b.startAt)} → {hm(b.endAt)}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={b.status === "RESERVED" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 border-0" : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-0"}>{b.status === "RESERVED" ? "Réservée" : "En attente"}</Badge>
            <Button size="icon" variant="ghost" onClick={() => delBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="h-9 w-9 rounded-lg text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function SpecialtiesTab() {
  const [specs, setSpecs] = useState([]);
  const load = useCallback(() => api.get("/admin/specialties").then((r) => setSpecs(r.data)).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (id, body) => { try { await api.patch(`/admin/specialties/${id}`, body); load(); } catch (e) { err(e); } };
  return (
    <div className="space-y-2" data-testid="specialties-list">
      {specs.map((s) => (
        <Card key={s.id} className="rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid={`spec-${s.slug}`}>
          <div className="flex-1 min-w-[160px]"><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.slug}</div></div>
          <div className="flex items-center gap-2"><Label className="text-xs">Prévention</Label>
            <Select value={s.preventionMode || "facultatif"} onValueChange={(v) => patch(s.id, { preventionMode: v })}>
              <SelectTrigger data-testid={`spec-prevention-${s.slug}`} className="h-9 w-36 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>{PREVENTION_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="flex items-center gap-2"><Label className="text-xs">Active</Label>
            <Switch checked={!!s.active} onCheckedChange={(v) => patch(s.id, { active: v })} data-testid={`spec-active-${s.slug}`} /></div>
        </Card>
      ))}
    </div>
  );
}

function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [drafts, setDrafts] = useState({});
  const load = useCallback(() => api.get("/admin/partners").then((r) => { setPartners(r.data); setDrafts(Object.fromEntries(r.data.map((p) => [p.id, { urlTemplate: p.urlTemplate || "", baseUrl: p.baseUrl || "" }]))); }).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const save = async (p) => { try { await api.patch(`/admin/partners/${p.id}`, drafts[p.id]); toast.success(`${p.name} enregistré`); load(); } catch (e) { err(e); } };
  const toggle = async (p, v) => { try { await api.patch(`/admin/partners/${p.id}`, { active: v }); load(); } catch (e) { err(e); } };
  return (
    <div className="space-y-3" data-testid="partners-list">
      {partners.map((p) => (
        <Card key={p.id} className="rounded-2xl p-5 space-y-3" data-testid={`partner-${p.slug}`}>
          <div className="flex items-center justify-between">
            <div className="font-heading font-semibold">{p.name}</div>
            <div className="flex items-center gap-2"><Label className="text-xs">Active</Label>
              <Switch checked={!!p.active} onCheckedChange={(v) => toggle(p, v)} data-testid={`partner-active-${p.slug}`} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Modèle d'URL (variables : {"{specialtySlug}"}, {"{locationSlug}"})</Label>
            <Input value={drafts[p.id]?.urlTemplate || ""} onChange={(e) => setDrafts({ ...drafts, [p.id]: { ...drafts[p.id], urlTemplate: e.target.value } })} data-testid={`partner-template-${p.slug}`} className="h-11 rounded-xl font-mono text-xs" /></div>
          <div className="space-y-1.5"><Label className="text-xs">URL de base (repli)</Label>
            <Input value={drafts[p.id]?.baseUrl || ""} onChange={(e) => setDrafts({ ...drafts, [p.id]: { ...drafts[p.id], baseUrl: e.target.value } })} data-testid={`partner-base-${p.slug}`} className="h-11 rounded-xl font-mono text-xs" /></div>
          <Button onClick={() => save(p)} data-testid={`partner-save-${p.slug}`} className="rounded-xl h-10">Enregistrer</Button>
        </Card>
      ))}
    </div>
  );
}

export default function Admin() {
  return (
    <div className="min-h-screen bg-background">
      <StaffHeader />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Administration</h1>
        <p className="text-sm text-muted-foreground mb-6">Cabines & horaires, réservations, spécialités et plateformes de redirection.</p>
        <Tabs defaultValue="cabins">
          <TabsList className="rounded-xl h-11 mb-6 flex-wrap">
            <TabsTrigger value="cabins" data-testid="tab-cabins" className="rounded-lg gap-1.5"><Box className="h-4 w-4" /> Cabines & horaires</TabsTrigger>
            <TabsTrigger value="agenda" data-testid="tab-agenda" className="rounded-lg gap-1.5"><CalendarClock className="h-4 w-4" /> Agenda de la cabine</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-lg gap-1.5"><CalendarClock className="h-4 w-4" /> Réservations</TabsTrigger>
            <TabsTrigger value="specs" data-testid="tab-specs" className="rounded-lg gap-1.5"><Stethoscope className="h-4 w-4" /> Spécialités</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners" className="rounded-lg gap-1.5"><Link2 className="h-4 w-4" /> Plateformes</TabsTrigger>
          </TabsList>
          <TabsContent value="cabins"><CabinsTab /></TabsContent>
          <TabsContent value="agenda"><AgendaTab /></TabsContent>
          <TabsContent value="bookings"><BookingsTab /></TabsContent>
          <TabsContent value="specs"><SpecialtiesTab /></TabsContent>
          <TabsContent value="partners"><PartnersTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
